// 바우처 기관발행 — 관리자 화면 공용 헬퍼 (docs/바우처_기관발행_관리자화면_가이드.md)
//
// 네 화면(기관·Master·구매 확정·등록 내역)과 기관 상세, 이력 탭이 같은 값을 그립니다.
// 상태 라벨이나 금액 표기가 화면마다 어긋나면 같은 건이 두 가지로 읽히므로 해석은 여기 한 곳에만 둡니다.
//
// 서버 규칙(가이드 §2):
//   - 금액은 문자열 Decimal("9982.00"). 계산하지 않고 표시만 하므로 Number 로 바꿔 formatMoney 에 넘깁니다.
//   - 시각은 UTC(+00:00) ISO, 날짜(contract_end·register_until·deposited_on)는 KST 달력일 YYYY-MM-DD.
//   - 오류 detail 은 항상 객체 {code, message, …}. 분기는 code 로 합니다.
import { formatMoney } from '@/utils/currency'
import { formatUtcToTimeZone } from '@/utils/datetime'
import { parseApiError } from '@/utils/apiError'

export const KST = 'Asia/Seoul'

/** 기관 바우처 결제수단 코드. payment.payment_method = 'V' */
export const VOUCHER_PAYMENT_METHOD = 'V'

/** 서버 UTC 시각 → "YYYY-MM-DD HH:mm" (KST) */
export const fmtKst = (iso) => {
  if (!iso) return '-'
  const s = formatUtcToTimeZone(iso, KST)
  return s ? `${s.slice(0, 10)} ${s.slice(11, 16)}` : '-'
}

/** 오늘 KST 달력일 YYYY-MM-DD. 입금일 기본값 등에 씁니다. */
export const todayKst = () => {
  const s = formatUtcToTimeZone(new Date().toISOString(), KST)
  return s ? s.slice(0, 10) : new Date().toISOString().slice(0, 10)
}

/** 금액 표기. null/undefined 는 0 이 아니라 "-" 로 둡니다(값 없음과 0 원을 구분). */
export const money = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '-'
  return formatMoney(amount, currency || 'USD')
}

/** 원장처럼 부호가 뜻을 갖는 금액. +는 명시하고 −는 formatMoney 가 붙입니다. */
export const signedMoney = (amount, currency) => {
  if (amount === null || amount === undefined || amount === '') return '-'
  const n = Number(amount)
  return n > 0 ? `+${formatMoney(n, currency || 'USD')}` : formatMoney(n, currency || 'USD')
}

export const isPositive = (v) => Number(v) > 0

/** 할인율 "10.00" → "10%" */
export const percent = (v) => {
  if (v === null || v === undefined || v === '') return '-'
  const n = Number(v)
  return Number.isFinite(n) ? `${Number(n.toFixed(2))}%` : String(v)
}

/**
 * 목록 응답을 {items, total, page} 로 접습니다(가이드 §2 목록 봉투).
 *   페이지 목록: { items, total, page, size }  — organizations·masters·purchases·class-vouchers·vouchers·ledger·history
 *   배열:        [ … ]                         — organizations/{idx}/members·purchases, masters/without-master
 * /system/logs/history/vouchers 는 기존 이력 로그 봉투라 SystemLogsHistory 가 따로 읽습니다.
 */
export const pageOf = (res, fallbackPage = 1) => {
  const body = res?.data
  if (Array.isArray(body)) return { items: body, total: body.length, page: fallbackPage }
  const items = body?.items || []
  return { items, total: Number(body?.total ?? items.length) || 0, page: Number(body?.page) || fallbackPage }
}

/** 단건 응답 본문. /voucher/admin/* 단건은 객체 그대로 옵니다. */
export const bodyOf = (res) => res?.data

/** 오류 detail 객체. 바우처 API 는 항상 {code, message, …} 입니다. */
export const errorDetail = (e) => {
  const d = e?.response?.data?.detail
  return d && typeof d === 'object' && !Array.isArray(d) ? d : null
}
export const errorCode = (e) => errorDetail(e)?.code || ''
export const errorMessage = (e, fallback) => parseApiError(e?.response?.data?.detail, fallback)

/** 원장 항목 종류 6종(가이드 §2). 모르는 코드는 원문 그대로 보입니다. */
export const LEDGER_TYPES = [
  { value: 'PURCHASE', label: '구매 적립', kind: 'ok' },
  { value: 'PURCHASE_VOID', label: '구매 취소', kind: 'err' },
  { value: 'REGISTER', label: '등록 차감', kind: 'info' },
  { value: 'REGISTER_REVERSAL', label: '차감 되돌림', kind: 'default' },
  { value: 'WITHDRAW', label: '반환', kind: 'warn' },
  { value: 'ADJUST', label: '정정', kind: 'dark' },
]
export const ledgerTypeLabel = (t) => LEDGER_TYPES.find((x) => x.value === t)?.label || t || '-'
export const ledgerTypeKind = (t) => LEDGER_TYPES.find((x) => x.value === t)?.kind || 'default'

/** 구매(충전) 상태. 가이드 §3-3 의 status 값 그대로입니다. */
export const PURCHASE_STATUS = {
  REQUESTED: { label: '입금 대기', kind: 'warn' },
  CONFIRMED: { label: '확정', kind: 'ok' },
  CANCELLED: { label: '반려·철회', kind: 'default' },
  VOIDED: { label: '확정 취소', kind: 'err' },
}
export const purchaseStatusLabel = (s) => PURCHASE_STATUS[s]?.label || s || '-'
export const purchaseStatusKind = (s) => PURCHASE_STATUS[s]?.kind || 'default'

/**
 * 구매 행의 바우처 통화. voucher_currency 는 확정 시점 스냅샷이라 확정 전엔 null 이고,
 * 그때는 기관의 현재 통화(org_currency)를 씁니다(가이드 §2 행 필드).
 */
export const purchaseCurrency = (row) => row?.voucher_currency || row?.org_currency || 'USD'

/** 학생 등록(voucher) 상태 3종. PENDING·FAILED 는 복구 배치 대상이라 눈에 띄게 둡니다(가이드 §4-5). */
export const VOUCHER_STATUS = {
  REGISTERED: { label: '등록 완료', kind: 'ok' },
  PENDING: { label: '처리 중', kind: 'warn' },
  FAILED: { label: '실패', kind: 'err' },
}
export const voucherStatusLabel = (s) => VOUCHER_STATUS[s]?.label || s || '-'
export const voucherStatusKind = (s) => VOUCHER_STATUS[s]?.kind || 'default'
export const needsRecoveryCheck = (s) => s === 'PENDING' || s === 'FAILED'

/** 클래스 지정(class_voucher) 상태 */
export const CLASS_VOUCHER_STATUS = {
  ACTIVE: { label: '진행 중', kind: 'ok' },
  CLOSED: { label: '종료', kind: 'default' },
}
export const classVoucherStatusLabel = (s) => CLASS_VOUCHER_STATUS[s]?.label || s || '-'
export const classVoucherStatusKind = (s) => CLASS_VOUCHER_STATUS[s]?.kind || 'default'

/** 기관 멤버 역할 */
export const MEMBER_ROLES = [
  { value: 'master', label: 'Master', kind: 'yellow' },
  { value: 'instructor', label: '강사', kind: 'info' },
]
export const memberRoleLabel = (r) => MEMBER_ROLES.find((x) => x.value === r)?.label || r || '-'
export const memberRoleKind = (r) => MEMBER_ROLES.find((x) => x.value === r)?.kind || 'default'

/**
 * 이력 이벤트 코드(가이드 §2). 기관 이력 · Master 상세 · 로그 › 이력 › 바우처 탭이 같은 코드를 씁니다.
 * 필터 선택 목록도 이 표에서 만듭니다.
 */
export const HISTORY_EVENTS = [
  { value: 'organization_created', label: '기관 등록', kind: 'ok' },
  { value: 'organization_updated', label: '기관 정보 수정', kind: 'default' },
  { value: 'organization_deactivated', label: '기관 비활성화', kind: 'err' },
  { value: 'organization_activated', label: '기관 재활성화', kind: 'ok' },
  { value: 'master_assigned', label: 'Master 지정', kind: 'yellow' },
  { value: 'master_released', label: 'Master 해제', kind: 'default' },
  { value: 'role_changed', label: '역할 변경', kind: 'default' },
  { value: 'instructor_invited', label: '강사 등록 요청', kind: 'warn' },
  { value: 'instructor_invite_cancelled', label: '등록 요청 취소', kind: 'default' },
  // 동의 근거는 details.consent.invite_idx 로 실립니다(칩으로 그대로 보입니다).
  { value: 'instructor_added', label: '강사 등록(동의 완료)', kind: 'info' },
  { value: 'instructor_removed', label: '강사 해제', kind: 'default' },
  { value: 'member_withdrawn', label: '탈퇴로 자동 해제', kind: 'warn' },
  { value: 'withdrawal_blocked', label: 'Master 탈퇴 시도', kind: 'warn' },
  { value: 'purchase_requested', label: '구매 요청', kind: 'info' },
  { value: 'purchase_cancelled', label: '구매 요청 철회', kind: 'default' },
  { value: 'purchase_confirmed', label: '구매 확정', kind: 'ok' },
  { value: 'purchase_rejected', label: '구매 반려', kind: 'default' },
  { value: 'purchase_voided', label: '구매 확정 취소', kind: 'err' },
  { value: 'balance_withdrawn', label: '잔액 반환', kind: 'warn' },
  { value: 'balance_adjusted', label: '잔액 정정', kind: 'dark' },
  { value: 'class_voucher_assigned', label: '클래스 지정', kind: 'info' },
  { value: 'class_voucher_updated', label: '클래스 지정 수정', kind: 'default' },
  { value: 'class_voucher_closed', label: '클래스 지정 종료', kind: 'default' },
  { value: 'voucher_registered', label: '학생 등록', kind: 'ok' },
  { value: 'voucher_failed', label: '학생 등록 실패', kind: 'err' },
  { value: 'voucher_recovered', label: '학생 등록 복구', kind: 'info' },
]
export const historyEventLabel = (e) => HISTORY_EVENTS.find((x) => x.value === e)?.label || e || '-'
export const historyEventKind = (e) => HISTORY_EVENTS.find((x) => x.value === e)?.kind || 'default'

const fmtVal = (v) => (v === null || v === undefined || v === '' ? '∅' : typeof v === 'object' ? JSON.stringify(v) : String(v))

/** 이력 행의 변경 전후 값. organization_updated 의 details.changes = {필드: {before, after}} */
export const historyChanges = (row) => {
  const c = row?.details?.changes
  if (!c || typeof c !== 'object') return []
  return Object.entries(c).map(([key, v]) => ({ key, before: fmtVal(v?.before), after: fmtVal(v?.after) }))
}

/**
 * changes 밖의 details 값(purchase_confirmed 의 credited_amount 등). 이벤트마다 키가 달라 들어온 키를 그대로 칩으로 펼칩니다.
 * 금액은 참고값이고 정본은 원장입니다(가이드 §2).
 */
export const historyDetailChips = (row) => {
  const d = row?.details
  if (!d || typeof d !== 'object') return []
  return Object.entries(d)
    .filter(([k, v]) => k !== 'changes' && k !== 'changed_fields' && v !== null && v !== undefined && v !== '')
    .map(([key, v]) => ({ key, value: fmtVal(v) }))
}

/** 이력 수행자. 배치·시스템은 null 입니다. */
export const historyActorLabel = (actor) => {
  if (!actor) return '시스템'
  return actor.name || actor.email || `#${actor.idx}`
}
export const historyActorType = (actor) => {
  if (!actor) return ''
  return actor.account_type === 'emp' ? '관리자' : '강사 · 학생'
}

/** 잔액 정정(adjust) 권한. 서버가 403 VOUCHER_ADJUST_FORBIDDEN 으로 최종 판정하고, 화면은 버튼만 숨깁니다. */
export const ADJUST_ROLES = ['super_admin', 'finance_manager']

/**
 * 통화 선택지. 서버가 등록되지 않은 통화를 400 VOUCHER_CURRENCY_UNKNOWN 으로 거부하므로
 * 목록은 서버 화폐 테이블(/common/currencies)에서 받습니다. 실패하면 USD 하나만 둡니다 — 기본값이자 권장 통화입니다.
 * @param {Function} axios this.$axios
 * @returns {Promise<string[]>}
 */
export const loadCurrencyCodes = async (axios) => {
  try {
    const res = await axios({ method: 'get', url: '/common/currencies', loadingBar: false })
    const list = Array.isArray(res.data) ? res.data : res.data?.currencies || []
    const codes = list.map((c) => c.code).filter(Boolean)
    if (!codes.includes('USD')) codes.unshift('USD')
    return codes
  } catch (e) {
    console.error('화폐 목록 로드 실패:', e)
    return ['USD']
  }
}

/**
 * 목록 조회 실패 문구. 목록 화면은 실패를 모달로 띄우지 않고 카드 안에 이 문구를 그립니다.
 * 모달로 띄우면 뒤의 빈 목록(모찌 "없습니다")과 겹쳐 "데이터가 없어서 오류" 로 읽히고,
 * 빈 상태만 그리면 서버 오류가 "0건" 으로 묻힙니다(SystemAccessLogCard 와 같은 이유).
 * 빈 목록은 200 + items: [] 로 오므로 여기로 오지 않습니다.
 */
export const loadErrorText = (e) => {
  const status = e?.response?.status
  // 응답이 없으면 브라우저가 상태 코드를 숨긴 경우입니다. 서버가 500 을 내면서 CORS 헤더를 빠뜨리면
  // 콘솔에는 "blocked by CORS policy" + 500 이 찍히고 axios 는 ERR_NETWORK 만 받습니다(2026-09-22 실제 사례).
  // "연결 실패" 로 단정하면 서버 오류를 네트워크 문제로 오독하므로 두 가능성을 함께 적습니다.
  if (!status) return '서버 응답을 받지 못했습니다. 콘솔에 500 이 보이면 서버 오류(백엔드 확인 필요)이고, 아니면 네트워크 · API 주소 문제입니다.'
  // 라우트가 없으면 detail 이 문자열("Not Found")이거나 HTML 이 옵니다. 바우처 API 오류는 detail 이 객체입니다.
  if (status === 404 && !errorDetail(e)) return '서버에 이 API 가 없습니다 (404). 바우처 API 가 올라간 서버인지 확인해 주세요.'
  // 500 본문은 텍스트 "Internal Server Error" 라 detail 이 없습니다. 서버 로그를 봐야 하는 건이라 그렇게 적습니다.
  if (status >= 500) return `서버 오류입니다 (${status}). 백엔드 오류 로그 확인이 필요합니다.`
  if (status === 401) return '로그인이 만료됐습니다 (401). 다시 로그인해 주세요.'
  if (status === 403) return `${errorMessage(e, '이 목록을 볼 권한이 없습니다.')} (403)`
  return `${errorMessage(e, '잠시 후 다시 시도해 주세요.')} (${status})`
}
